import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Heart, MapPin, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { reelsApi, toAbsolute } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { DeleteReelDialog, EditReelModal, ReelOptionsMenu, getReelDeleteErrorMessage } from "@/components/ReelManagement";
import { ReportModal } from "@/components/PostCard";
import ReelModal from "@/components/ReelModal";
import ReelCommentsDrawer from "@/components/ReelCommentsDrawer";
import { usePassportShare } from "@/components/messages/PassportShareProvider";

export default function ProfileReelsGrid({ handle, onCountChange }) {
    const { user } = useAuth() || {};
    const { openPassportShare } = usePassportShare();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editReel, setEditReel] = useState(null);
    const [deleteReel, setDeleteReel] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [reportReel, setReportReel] = useState(null);
    const [activeReel, setActiveReel] = useState(null);
    const [commentReel, setCommentReel] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response = await reelsApi.list({ creator: handle, limit: 40 });
            const next = response.items || [];
            setItems(next);
            onCountChange?.(next.length);
        } catch {
            setItems([]);
            onCountChange?.(0);
        } finally {
            setLoading(false);
        }
    }, [handle, onCountChange]);

    useEffect(() => {
        load();
    }, [load]);

    const sorted = useMemo(() => [...items].sort((a, b) => `${b.created_at || ""}`.localeCompare(a.created_at || "")), [items]);

    const removeLocal = (reelId) => {
        setItems((prev) => {
            const next = prev.filter((item) => item.id !== reelId);
            onCountChange?.(next.length);
            return next;
        });
    };

    const confirmDelete = async (reel) => {
        setDeleting(true);
        try {
            await reelsApi.remove(reel.id);
            removeLocal(reel.id);
            toast.success("Reel deleted successfully.");
            setDeleteReel(null);
            // Close modal if the deleted reel was active
            if (activeReel?.id === reel.id) setActiveReel(null);
        } catch (error) {
            toast.error(getReelDeleteErrorMessage(error));
        } finally {
            setDeleting(false);
        }
    };

    // Like handler — updates local state optimistically
    const handleLike = async (reel) => {
        try {
            const result = await reelsApi.like(reel.id);
            setItems((prev) =>
                prev.map((item) =>
                    item.id === reel.id
                        ? { ...item, likes: result.likes, liked: result.liked }
                        : item
                )
            );
            if (activeReel?.id === reel.id) {
                setActiveReel((prev) => ({ ...prev, likes: result.likes, liked: result.liked }));
            }
        } catch { /* silent */ }
    };

    // Save handler
    const handleSave = async (reel) => {
        try {
            await reelsApi.save(reel.id);
            toast.success("Reel saved.");
        } catch { toast.error("Could not save reel."); }
    };

    // Share handler
    const handleShare = async (reel) => {
        try {
            openPassportShare({ shareRef: { entity_type: "reel", entity_id: reel.id }, sourceLabel: "this Reel" });
        } catch { toast.error("Could not share reel."); }
    };

    if (loading) return <div className="text-white/50 text-sm py-10 text-center">Loading reels…</div>;
    if (!sorted.length) return <div className="text-white/50 text-sm py-10 text-center" data-testid="profile-reels-empty">No reels yet.</div>;

    return (
        <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="profile-reels-grid">
                {sorted.map((reel) => {
                    const isOwner = user?.id === reel.user_id;
                    return (
                        <div key={reel.id} className="rounded-[28px] overflow-hidden cursor-pointer group" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={`profile-reel-card-${reel.id}`}>
                            <div className="relative aspect-[9/16] bg-black">
                                <video src={toAbsolute(reel.media_url)} poster={toAbsolute(reel.poster_url) || undefined} muted playsInline preload="metadata" className="w-full h-full object-cover" data-testid={`profile-reel-video-${reel.id}`} />
                                {/* Gradient overlay */}
                                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.04), rgba(0,0,0,0.8))" }} />
                                {/* Top: location badge + options menu */}
                                <div className="absolute left-4 right-4 top-4 flex items-start justify-between gap-3 z-10">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em]" style={{ background: "rgba(212,175,55,0.16)", color: "var(--gold)" }} data-testid={`profile-reel-location-${reel.id}`}>
                                        <MapPin size={10} /> {reel.location || reel.country || "Passport"}
                                    </span>
                                    <ReelOptionsMenu
                                        reel={reel}
                                        isOwner={isOwner}
                                        onEdit={setEditReel}
                                        onDelete={setDeleteReel}
                                        onReport={setReportReel}
                                        onNotInterested={() => {
                                            removeLocal(reel.id);
                                            toast.success("We'll show less like this.");
                                        }}
                                    />
                                </div>
                                {/* Clickable overlay — opens ReelModal instead of navigating */}
                                <button
                                    className="absolute inset-0 z-0 w-full h-full"
                                    onClick={() => setActiveReel(reel)}
                                    data-testid={`profile-reel-open-${reel.id}`}
                                    aria-label="Open reel"
                                />
                                {/* Bottom info */}
                                <div className="absolute left-4 right-4 bottom-4 z-10 pointer-events-none">
                                    <div className="font-serif text-2xl text-white leading-tight line-clamp-2 mb-2" data-testid={`profile-reel-caption-${reel.id}`}>{reel.caption}</div>
                                    <div className="flex items-center justify-end gap-3 text-xs text-white/75">
                                        <div className="flex items-center gap-2.5">
                                            <span className="inline-flex items-center gap-1"><Eye size={11} /> {reel.views || 0}</span>
                                            <span className="inline-flex items-center gap-1"><Heart size={11} /> {reel.likes || 0}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Immersive ReelModal — replaces the legacy /reel/:id navigation */}
            <AnimatePresence>
                {activeReel && (
                    <ReelModal
                        reel={activeReel}
                        user={user}
                        onClose={() => setActiveReel(null)}
                        onLike={() => handleLike(activeReel)}
                        onSave={() => handleSave(activeReel)}
                        onShare={() => handleShare(activeReel)}
                        onOpenComments={() => setCommentReel(activeReel)}
                        onReport={() => setReportReel(activeReel)}
                        onPrev={() => {
                            const index = sorted.findIndex((r) => r.id === activeReel.id);
                            if (index > 0) setActiveReel(sorted[index - 1]);
                        }}
                        onNext={() => {
                            const index = sorted.findIndex((r) => r.id === activeReel.id);
                            if (index < sorted.length - 1) setActiveReel(sorted[index + 1]);
                        }}
                    />
                )}
            </AnimatePresence>

            <ReelCommentsDrawer reel={commentReel} open={!!commentReel} onClose={() => setCommentReel(null)} />
            <EditReelModal open={!!editReel} reel={editReel} onClose={() => setEditReel(null)} onSaved={(updated) => setItems((prev) => prev.map((item) => item.id === updated.id ? { ...item, ...updated } : item))} />
            <DeleteReelDialog open={!!deleteReel} reel={deleteReel} deleting={deleting} onClose={() => !deleting && setDeleteReel(null)} onConfirm={confirmDelete} />
            <ReportModal open={!!reportReel} onClose={() => setReportReel(null)} targetType="reel" targetId={reportReel?.id} />
        </>
    );
}